import React, { useState, useEffect } from "react";
import PageHeader from "../components/PageHeader";
import { useDashboardStats, useLeads, useTasks, useCallLogs, useAccounting } from "../../hooks/useFirebase";
import { useTeam } from "../../hooks/useFirebase";

const Dashboard = () => {
  const { stats, loading: statsLoading } = useDashboardStats();
  const { leads } = useLeads();
  const { tasks } = useTasks();
  const { callLogs } = useCallLogs();
  const { invoices, expenses } = useAccounting();
  const { teamMembers } = useTeam();

  const [period, setPeriod] = useState("month");

  if (statsLoading || !stats) {
    return (
      <div className="admin-loading">
        <div className="spinner-wrap">
          <div className="spinner" />
          <p className="spinner-text">Loading dashboard data...</p>
        </div>
      </div>
    );
  }

  // ==========================================
  // CALCULATIONS
  // ==========================================
  const totalRevenue = invoices.filter((i) => i.status === "paid").reduce((s, i) => s + parseFloat(i.grandTotal || i.total || 0), 0);
  const totalPendingPayment = invoices.filter((i) => i.status !== "paid").reduce((s, i) => s + parseFloat(i.grandTotal || i.total || 0), 0);
  const totalExpenses = expenses.reduce((s, e) => s + parseFloat(e.amount || 0), 0);
  const netProfit = totalRevenue - totalExpenses;
  const totalGST = invoices.reduce((s, i) => s + parseFloat(i.totalGST || 0), 0);

  // Task items progress
  const totalAssignedItems = tasks.reduce((sum, t) => sum + (t.totalItems || t.assignedItems?.length || 0), 0);
  const totalCompletedItems = tasks.reduce((sum, t) => sum + (t.assignedItems || []).filter((i) => i.status === "completed").length, 0);
  const itemsProgress = totalAssignedItems > 0 ? Math.round((totalCompletedItems / totalAssignedItems) * 100) : 0;

  // Conversion rate
  const conversionRate = stats.totalLeads > 0 ? ((stats.convertedLeads / stats.totalLeads) * 100).toFixed(1) : "0.0";

  // Recent leads
  const recentLeads = leads.slice(0, 6);

  // Recent tasks
  const recentTasks = tasks.slice(0, 5);

  // Recent invoices
  const recentInvoices = invoices.slice(0, 5);

  // Recent calls
  const recentCalls = callLogs.slice(0, 5);

  // Team performance
  const getTeamPerformance = () => {
    const memberMap = {};
    teamMembers.forEach((m) => {
      memberMap[m.id] = {
        id: m.id,
        name: m.name,
        role: m.role,
        isActive: m.isActive !== false,
        totalTasks: 0,
        completedTasks: 0,
        inProgressTasks: 0,
        overdueTasks: 0,
        totalItems: 0,
        completedItems: 0,
      };
    });
    tasks.forEach((task) => {
      const member = memberMap[task.assignedTo];
      if (member) {
        member.totalTasks++;
        if (task.status === "completed") member.completedTasks++;
        else if (task.status === "in_progress") member.inProgressTasks++;
        member.totalItems += task.totalItems || task.assignedItems?.length || 0;
        member.completedItems += (task.assignedItems || []).filter((i) => i.status === "completed").length;
        if (task.status !== "completed" && task.dueDate < new Date().toISOString().split("T")[0]) member.overdueTasks++;
      }
    });
    return Object.values(memberMap);
  };

  const teamPerformance = getTeamPerformance();

  // Monthly data for chart
  const getMonthlyData = () => {
    const months = [];
    for (let i = 5; i >= 0; i--) {
      const d = new Date();
      d.setMonth(d.getMonth() - i);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      months.push({
        key,
        label: d.toLocaleDateString("en-IN", { month: "short" }),
        leads: leads.filter((l) => (l.createdAt || "").startsWith(key)).length,
        calls: callLogs.filter((c) => (c.calledAt || "").startsWith(key)).length,
        revenue: invoices.filter((i) => (i.createdAt || "").startsWith(key) && i.status === "paid").reduce((s, i) => s + parseFloat(i.grandTotal || i.total || 0), 0),
      });
    }
    return months;
  };

  const monthlyData = getMonthlyData();
  const maxChartValue = Math.max(...monthlyData.map((m) => Math.max(m.leads, m.calls)), 1);

  // ==========================================
  // RENDER
  // ==========================================
  return (
    <div>
      <PageHeader title="Dashboard" breadcrumbs={["Admin", "Dashboard"]} />

      {/* ========== MAIN STATS ROW ========== */}
      <div className="admin-grid admin-grid-4" style={{ marginTop: 24, marginBottom: 20 }}>
        {[
          { label: "Total Leads", value: stats.totalLeads, icon: "🎯", cls: "qs-blue", detail: `${stats.newLeads} new` },
          { label: "Total Calls", value: stats.totalCalls, icon: "📞", cls: "qs-green", detail: `${stats.answeredCalls} answered` },
          { label: "Converted", value: stats.convertedLeads, icon: "✅", cls: "qs-purple", detail: `${conversionRate}% conversion` },
          { label: "Total Revenue", value: `₹${totalRevenue.toLocaleString("en-IN")}`, icon: "💰", cls: "qs-orange", detail: `₹${totalPendingPayment.toLocaleString("en-IN")} pending` },
        ].map((stat, i) => (
          <div key={i} className={`quick-stat ${stat.cls}`}>
            <div className="quick-stat-icon">{stat.icon}</div>
            <div className="quick-stat-info">
              <div className="quick-stat-label">{stat.label}</div>
              <div className="quick-stat-value">{stat.value}</div>
              <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 2 }}>{stat.detail}</div>
            </div>
          </div>
        ))}
      </div>

      {/* ========== SECONDARY STATS ========== */}
      <div className="admin-grid admin-grid-5" style={{ marginBottom: 24 }}>
        {[
          { label: "New Leads", value: stats.newLeads, icon: "🆕", cls: "qs-blue" },
          { label: "Interested", value: stats.interestedLeads, icon: "👍", cls: "qs-green" },
          { label: "Tasks Pending", value: stats.pendingTasks, icon: "⏳", cls: "qs-orange" },
          { label: "Overdue", value: stats.overdueTasks, icon: "⚠️", cls: "qs-red" },
          { label: "Items Done", value: `${totalCompletedItems}/${totalAssignedItems}`, icon: "📦", cls: "qs-purple" },
        ].map((item, i) => (
          <div key={i} className={`quick-stat ${item.cls}`}>
            <div className="quick-stat-icon">{item.icon}</div>
            <div className="quick-stat-info">
              <div className="quick-stat-label">{item.label}</div>
              <div className="quick-stat-value">{item.value}</div>
            </div>
          </div>
        ))}
      </div>

      {/* ========== FINANCIAL OVERVIEW ========== */}
      <div className="admin-grid admin-grid-4" style={{ marginBottom: 24 }}>
        {[
          { label: "Revenue", value: `₹${totalRevenue.toLocaleString("en-IN")}`, icon: "💰", color: "#22c55e", bg: "#f0fdf4" },
          { label: "Expenses", value: `₹${totalExpenses.toLocaleString("en-IN")}`, icon: "📉", color: "#ef4444", bg: "#fef2f2" },
          { label: "Net Profit", value: `₹${netProfit.toLocaleString("en-IN")}`, icon: "📈", color: "#2563eb", bg: "#eff6ff" },
          { label: "GST Collected", value: `₹${totalGST.toLocaleString("en-IN")}`, icon: "🧾", color: "#9333ea", bg: "#faf5ff" },
        ].map((item, i) => (
          <div key={i} className="stat-card" style={{ borderLeft: `4px solid ${item.color}` }}>
            <div className="stat-inner">
              <div className="stat-text">
                <div className="stat-label">{item.label}</div>
                <div className="stat-value">{item.value}</div>
              </div>
              <div className="stat-icon" style={{ background: item.bg }}>
                <span style={{ color: item.color }}>{item.icon}</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* ========== CHART + SUMMARY ========== */}
      <div className="admin-grid admin-grid-2" style={{ marginBottom: 24 }}>
        {/* Bar Chart */}
        <div className="chart-container">
          <div className="chart-header">
            <h3>📊 Monthly Performance</h3>
            <div className="chart-btns">
              <button className="active">Last 6 Months</button>
            </div>
          </div>
          <div className="bar-chart">
            {monthlyData.map((month, i) => (
              <div key={i} className="bar-group">
                <div className="bars">
                  <div
                    className="bar bar-leads"
                    style={{ height: `${(month.leads / maxChartValue) * 100}%` }}
                    title={`Leads: ${month.leads}`}
                  />
                  <div
                    className="bar bar-calls"
                    style={{ height: `${(month.calls / maxChartValue) * 100}%` }}
                    title={`Calls: ${month.calls}`}
                  />
                </div>
                <span className="bar-label">{month.label}</span>
              </div>
            ))}
          </div>
          <div className="bar-chart-legend">
            <div className="legend-item">
              <div className="legend-dot leads" />
              <span className="legend-label">Leads</span>
            </div>
            <div className="legend-item">
              <div className="legend-dot calls" />
              <span className="legend-label">Calls</span>
            </div>
          </div>
        </div>

        {/* Today's Summary */}
        <div className="summary-panel">
          <h3>📋 Today's Summary</h3>
          <div className="summary-list">
            {[
              { label: "Total Leads", value: stats.totalLeads, color: "blue" },
              { label: "New Leads", value: stats.newLeads, color: "blue" },
              { label: "Answered Calls", value: stats.answeredCalls, color: "green" },
              { label: "Callback Due", value: stats.callbackCalls, color: "orange" },
              { label: "Tasks Pending", value: stats.pendingTasks, color: "orange" },
              { label: "Tasks Completed", value: stats.completedTasks, color: "green" },
              { label: "Overdue Tasks", value: stats.overdueTasks, color: "red" },
              { label: "Invoices Pending", value: stats.pendingInvoices, color: "orange" },
              { label: "Payments Received", value: `₹${totalRevenue.toLocaleString("en-IN")}`, color: "green" },
              { label: "Pending Amount", value: `₹${totalPendingPayment.toLocaleString("en-IN")}`, color: "red" },
            ].map((item, i) => (
              <div key={i} className="summary-item">
                <div className="item-left">
                  <div className={`item-dot ${item.color}`} />
                  <span className="item-label">{item.label}</span>
                </div>
                <span className={`item-value ${item.color}`}>{item.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ========== SALES FUNNEL ========== */}
      <div className="funnel-section" style={{ marginBottom: 24 }}>
        <h3>📊 Sales Funnel</h3>
        {[
          { stage: "Total Leads", value: stats.totalLeads, color: "color-blue" },
          { stage: "Contacted", value: stats.totalCalls > 0 ? Math.min(stats.totalCalls, stats.totalLeads) : 0, color: "color-blue-light" },
          { stage: "Interested", value: stats.interestedLeads, color: "color-green-light" },
          { stage: "Converted", value: stats.convertedLeads, color: "color-green" },
        ].map((stage, i) => (
          <div key={i} className="funnel-step">
            <div className="funnel-label">
              <span className="step-name">{stage.stage}</span>
              <span className="step-value">{stage.value}</span>
            </div>
            <div className="funnel-bar">
              <div
                className={`funnel-bar-fill ${stage.color}`}
                style={{ width: `${stats.totalLeads ? (stage.value / stats.totalLeads) * 100 : 0}%` }}
              >
                <span className="fill-percent">
                  {stats.totalLeads ? Math.round((stage.value / stats.totalLeads) * 100) : 0}%
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* ========== RECENT LEADS + RECENT TASKS ========== */}
      <div className="admin-grid admin-grid-2" style={{ marginBottom: 24 }}>
        {/* Recent Leads */}
        <div className="data-table-wrapper">
          <div className="data-table-toolbar">
            <div className="toolbar-title">
              <h3>📋 Recent Leads</h3>
              <span style={{ fontSize: 12, color: "#9ca3af" }}>{leads.length} total</span>
            </div>
          </div>
          <div className="data-table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Lead</th>
                  <th>Source</th>
                  <th>Status</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {recentLeads.length === 0 ? (
                  <tr>
                    <td colSpan="4">
                      <div className="empty-state">
                        <div className="empty-icon">📭</div>
                        <p>No leads yet.</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  recentLeads.map((lead) => (
                    <tr key={lead.id}>
                      <td>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <div className="admin-avatar small">
                            <span>{(lead.name || lead.businessName || "?").split(" ").map((n) => n[0]).join("").substring(0, 2)}</span>
                          </div>
                          <div>
                            <div className="font-bold text-14">{lead.businessName || lead.name}</div>
                            <div className="text-12 text-gray-400">{lead.phone || lead.contactNumber || "—"}</div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className="status-badge badge-source">{lead.category || lead.source || "—"}</span>
                      </td>
                      <td>
                        <span className={`status-badge badge-${lead.status}`}>
                          {lead.status?.replace("_", " ")}
                        </span>
                      </td>
                      <td>
                        <span className="text-12 text-gray-400">
                          {lead.createdAt ? new Date(lead.createdAt).toLocaleDateString("en-IN") : "—"}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Recent Tasks */}
        <div className="data-table-wrapper">
          <div className="data-table-toolbar">
            <div className="toolbar-title">
              <h3>✅ Recent Tasks</h3>
              <span style={{ fontSize: 12, color: "#9ca3af" }}>{tasks.length} total</span>
            </div>
          </div>
          <div style={{ padding: 12 }}>
            <div className="task-list">
              {recentTasks.length === 0 ? (
                <div style={{ textAlign: "center", padding: 32, color: "#9ca3af" }}>
                  <p className="text-24 mb-2">📋</p>
                  <p>No tasks yet.</p>
                </div>
              ) : (
                recentTasks.map((task) => {
                  const isOverdue = task.status !== "completed" && task.dueDate < new Date().toISOString().split("T")[0];
                  const completedItems = (task.assignedItems || []).filter((i) => i.status === "completed").length;
                  const totalItems = task.totalItems || task.assignedItems?.length || 0;
                  const progress = task.progress || (totalItems > 0 ? Math.round((completedItems / totalItems) * 100) : 0);

                  return (
                    <div
                      key={task.id}
                      className={`task-card ${task.status === "completed" ? "task-completed" : isOverdue ? "task-overdue" : task.priority === "high" ? "task-high" : "task-medium"}`}
                      style={{ marginBottom: 8 }}
                    >
                      <div className="task-card-inner" style={{ flexDirection: "column", gap: 6 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", width: "100%" }}>
                          <div>
                            <p className="task-title" style={{ fontSize: 13 }}>{task.title}</p>
                            <div className="task-meta" style={{ marginTop: 4 }}>
                              <span>👤 {task.assignedName}</span>
                              <span className={isOverdue ? "overdue" : ""}>📅 {task.dueDate}</span>
                              <span>📦 {completedItems}/{totalItems}</span>
                            </div>
                          </div>
                          <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                            <span className={`status-badge badge-${task.status}`} style={{ fontSize: 10 }}>
                              {task.status.replace("_", " ")}
                            </span>
                          </div>
                        </div>
                        <div style={{ width: "100%" }}>
                          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 2 }}>
                            <span className="text-12 text-gray-500">Progress</span>
                            <span className="text-12 font-bold" style={{ color: progress === 100 ? "#16a34a" : "#3b82f6" }}>{progress}%</span>
                          </div>
                          <div style={{ width: "100%", height: 4, backgroundColor: "#f3f4f6", borderRadius: 9999, overflow: "hidden" }}>
                            <div style={{ width: `${progress}%`, height: "100%", backgroundColor: progress === 100 ? "#22c55e" : progress >= 50 ? "#3b82f6" : "#f97316", borderRadius: 9999 }} />
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ========== TEAM PERFORMANCE ========== */}
      <div className="leaderboard-section" style={{ marginBottom: 24 }}>
        <h3>🏆 Team Performance</h3>
        <div style={{ overflowX: "auto" }}>
          <table className="data-table">
            <thead>
              <tr>
                <th>Team Member</th>
                <th>Role</th>
                <th>Tasks</th>
                <th>Completed</th>
                <th>Items Done</th>
                <th>Progress</th>
                <th>Overdue</th>
              </tr>
            </thead>
            <tbody>
              {teamPerformance.map((member, i) => {
                const itemProgress = member.totalItems > 0 ? Math.round((member.completedItems / member.totalItems) * 100) : 0;
                return (
                  <tr key={member.id || i}>
                    <td>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <div className="admin-avatar small">
                          <span>{member.name?.split(" ").map((n) => n[0]).join("")}</span>
                        </div>
                        <div>
                          <div className="font-bold text-14">{member.name}</div>
                          {!member.isActive && <span className="text-12 text-gray-400">Inactive</span>}
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className={`status-badge badge-${member.role}`}>{member.role}</span>
                    </td>
                    <td className="font-semibold">{member.totalTasks}</td>
                    <td className="font-bold" style={{ color: "#16a34a" }}>{member.completedTasks}</td>
                    <td className="font-semibold">
                      {member.completedItems} / {member.totalItems}
                    </td>
                    <td>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <div style={{ width: 70, height: 6, backgroundColor: "#f3f4f6", borderRadius: 9999, overflow: "hidden" }}>
                          <div
                            style={{
                              width: `${itemProgress}%`,
                              height: "100%",
                              backgroundColor: itemProgress >= 80 ? "#22c55e" : itemProgress >= 40 ? "#3b82f6" : "#f97316",
                              borderRadius: 9999,
                            }}
                          />
                        </div>
                        <span className="text-12 font-bold">{itemProgress}%</span>
                      </div>
                    </td>
                    <td>
                      {member.overdueTasks > 0 ? (
                        <span className="status-badge badge-overdue">{member.overdueTasks}</span>
                      ) : (
                        <span className="status-badge badge-completed">0</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* ========== RECENT INVOICES + CALLS ========== */}
      <div className="admin-grid admin-grid-2">
        {/* Recent Invoices */}
        <div className="data-table-wrapper">
          <div className="data-table-toolbar">
            <div className="toolbar-title">
              <h3>📄 Recent Invoices</h3>
              <span style={{ fontSize: 12, color: "#9ca3af" }}>{invoices.length} total</span>
            </div>
          </div>
          <div className="data-table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Invoice #</th>
                  <th>Client</th>
                  <th>Total</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {recentInvoices.length === 0 ? (
                  <tr>
                    <td colSpan="4">
                      <div className="empty-state">
                        <div className="empty-icon">📄</div>
                        <p>No invoices yet.</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  recentInvoices.map((inv) => (
                    <tr key={inv.id}>
                      <td className="font-bold text-blue-600">{inv.invoiceNo}</td>
                      <td className="font-semibold">{inv.clientName}</td>
                      <td className="font-bold">₹{parseFloat(inv.grandTotal || inv.total || 0).toLocaleString("en-IN")}</td>
                      <td>
                        <span className={`status-badge badge-${inv.status}`}>{inv.status}</span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Recent Calls */}
        <div className="data-table-wrapper">
          <div className="data-table-toolbar">
            <div className="toolbar-title">
              <h3>📞 Recent Calls</h3>
              <span style={{ fontSize: 12, color: "#9ca3af" }}>{stats.totalCalls} total</span>
            </div>
          </div>
          <div className="data-table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Lead</th>
                  <th>Disposition</th>
                  <th>Duration</th>
                </tr>
              </thead>
              <tbody>
                {recentCalls.length === 0 ? (
                  <tr>
                    <td colSpan="3">
                      <div className="empty-state">
                        <div className="empty-icon">📞</div>
                        <p>No call logs yet.</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  recentCalls.map((call) => (
                    <tr key={call.id}>
                      <td className="font-bold text-gray-900">{call.leadName}</td>
                      <td>
                        <span className={`status-badge badge-${call.disposition}`}>
                          {call.disposition?.replace("_", " ")}
                        </span>
                      </td>
                      <td>⏱ {call.duration || "—"}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;